import { defaultGroupSettings } from "../../view/shared/helpers"
import { setCurrentPage } from "../reducers/navigation"
import { backendAPI, blockchainAPI } from "../api"
import { LOADED_VSLA_MEMBERS } from "./members/getVslaMembers"
import { triggerAction } from "../constants"
import { safeApiCall } from "../api/offline"
import {
  GROUP_CLEARED,
  GROUP_ADDED_LOCALLY,
  GROUP_CREATED_SUCCESS,
  GROUP_SYNC_FAILED,
} from "../reducers/base/group"
import {
  addToCache,
  db,
  deleteCacheByKey,
  patchCacheItem,
  resetRequestsErrors,
} from "../../view/shared/utilities/cache"
import { BLOCKCHAIN_DATA_LOADED } from "../reducers/vslaDataReducer"
import { precacheData } from "./precache"

export const createVslaError = (error) => ({
  type: "CREATE_VSLA_ERROR",
  error: error,
})

export const createVsla = (vsla) => ({
  type: "CREATE_VSLA",
  vsla,
})

export const createGroupError = (bool) => ({
  type: "CREATE_GROUP_ERROR",
  groupError: bool,
})

export const checkGroupName = (name) => async () => {
  const data = await safeApiCall(
    {
      cacheEnabled: true,
      url: `/groups-hedera/names/${name}`,
    },
    async () => backendAPI.get(`/groups-hedera/names/${name}`),
  )
  return data
}

export function postCreateVsla(url, vsla, headers) {
  return async function (dispatch) {
    try {
      dispatch(triggerAction("IS_CREATING_VSLA", false))
      const isExist = checkGroupName(vsla.description)
      if (isExist.data && isExist.data.status === "Active") {
        throw new Error("duplicateGroup")
      }
      const res = await blockchainAPI.post(
        url,
        {
          ...vsla,
          metadata: {
            ...vsla?.metadata,
            ...defaultGroupSettings,
          },
        },
        headers,
      )
      dispatch(createVsla(res.data))
      dispatch(setCurrentPage("group-addFunds"))
      return Promise.resolve(res.data)
    } catch (error) {
      dispatch(createVslaError(error))
      return Promise.reject(error)
    }
  }
}

export function postCreateGroup(data) {
  return async function (dispatch, getState) {
    const { user, group } = getState()
    if (!user) {
      dispatch(createGroupError(true))
      console.error("Cannot create group: User not logged in.")
      return
    }

    if (group && !group.id && group.userId === user.id) {
      await deleteCacheByKey("groups", "cacheId", group.cacheId)
      await deleteCacheByKey("offlineRequests", "tempId", group.cacheId)
      dispatch(triggerAction(GROUP_CLEARED))
      await resetRequestsErrors()
    }

    const tempId = data.secretary.pubkey
    // 1. Create the optimistic entity and save it to its local table first.
    const optimisticGroup = {
      ...data.group,
      syncStatus: "pending",
      userId: user.id,
      cacheId: tempId,
      funds: data.accounts.map((type) => ({ type, balances: 0 }))
    }
    // `addToCache` returns the auto-generated `cacheId`.
    await addToCache("groups", data, user.id, tempId)
    const urlPart = `/groups-hedera/?requestId=${tempId}`
    const cachedUrl = `${window.REACT_APP_PWA_APP_DOMAIN}${urlPart}`
    try {
      const offlineRequests = await db.offlineRequests.toArray()
      const localMembers = offlineRequests.filter(
        ({ userId, entityType }) => userId === user?.id && entityType === "members",
      )
      dispatch(triggerAction(GROUP_ADDED_LOCALLY, optimisticGroup))
      dispatch(
        triggerAction(BLOCKCHAIN_DATA_LOADED, {
          ...data.blockChain,
          accounts: data.accounts,
        }),
      )
      dispatch(
        triggerAction(LOADED_VSLA_MEMBERS, [
          {
            ...data.secretary,
            syncStatus: "pending",
            error: null,
          },
          ...localMembers.map((m) => ({
            ...m.payload.data,
            cacheId: m.cacheId,
            syncStatus: m.status,
            error: m.error || null,
          })),
        ]),
      )
      const headers = {
        Authorization: `Bearer ${user.token}`,
      }
      dispatch(triggerAction("UPDATE_LOADER_STATE", true))
      dispatch(triggerAction("IS_CREATING_VSLA", true))
      const response = await safeApiCall(
        {
          // --- Core Request Config ---
          method: "POST",
          url: cachedUrl, // Include the tempId in the URL for tracking
          data: data,
          headers: headers,

          // --- Offline & Queueing Metadata ---
          tempId: tempId, // Using the cacheId from the 'groups' table
          entityType: "group",
          dependsOn: null,
          userId: user.id, // Pass the userId for cache updates

          // --- Redux Actions for automatic dispatch ---
          optimisticActions: [
            {
              type: GROUP_ADDED_LOCALLY,
              payload: optimisticGroup,
            },
            {
              type: BLOCKCHAIN_DATA_LOADED,
              payload: {
                ...data.blockChain,
                funds: data.accounts.map((type) => ({ type, balances: 0 })),
              },
            },
            {
              type: LOADED_VSLA_MEMBERS,
              payload: [
                {
                  ...data.secretary,
                  syncStatus: "pending",
                  error: null,
                },
              ],
            },
          ],
          successAction: [
            // { type: GROUP_CREATED_SUCCESS },
            // { type: BLOCKCHAIN_DATA_LOADED },
            // { type: LOADED_VSLA_MEMBERS },
          ],
          failAction: { type: GROUP_SYNC_FAILED },
        },
        // The actual API call callback
        async () => backendAPI.post(urlPart, data),
      )

      if (response && !response.data.queued) {
        console.log(
          `[Action] Group '${data.group.name}' created successfully online.`,
        )
        const { group: serverGroup, group_wallet, member } = response.data

        dispatch(
          triggerAction(GROUP_CREATED_SUCCESS, { ...serverGroup, tempId: tempId }),
        )
        dispatch(triggerAction(BLOCKCHAIN_DATA_LOADED, group_wallet))
        dispatch(triggerAction(LOADED_VSLA_MEMBERS, [member]))
        dispatch(precacheData()) // load data from server
        await deleteCacheByKey("groups", "cacheId", tempId) // completly remove the entry
        console.log(`[Action] Group '${data.group.name}' synced successfully.`)
      } else {
        dispatch(triggerAction("IS_CREATING_VSLA", false)) // data cached, disable loader
      }
      dispatch(setCurrentPage("group-main"))
    } catch (error) {
      dispatch(triggerAction("IS_CREATING_VSLA", false))
      console.error("[Action] Error during postCreateGroup execution:", error)
      // dispatch(createGroupError(true));
      if (!error.isAxiosError || error.response) {
        const errorMessage =
          error.response?.data?.detail || error.message || "Unknown error"
        dispatch(triggerAction(GROUP_SYNC_FAILED, { tempId, error: errorMessage }))
        await patchCacheItem("groups", tempId, {
          group: {
            ...data.group,
            syncStatus: "error",
            error: errorMessage,
          },
        })
        dispatch(setCurrentPage("group-createGroup"))
      }
    } finally {
      dispatch(triggerAction("UPDATE_LOADER_STATE", false))
    }
  }
}

export const updateGroupSettings = (data) => async (_, getState) => {
  const { group } = getState()
  try {
    const res = await safeApiCall(
      {
        cacheEnabled: true,
        url: `/groups-hedera/${group?.id}/settings`,
      },
      async () =>
        backendAPI.patch(`/groups-hedera/${group?.id}/settings`, {
          share_value: data.metadata.share_value,
          meta_info: data.metadata,
        }),
    )
    return Promise.resolve(res)
  } catch (error) {
    console.log("Error updating group settings: ", error)
    return Promise.reject(error)
  }
}

export const getProjects = () => async (dispatch) => {
  try {
    const { data } = await safeApiCall(
      {
        cacheEnabled: true,
        url: "/projects/",
      },
      async () => backendAPI.get("/projects/"),
    )
    dispatch(triggerAction("SET_PROJECTS", data))
  } catch (error) {
    console.log("Error: ", error)
  }
}
